import React, { useState, useRef } from 'react';

const MIN_COLUMN_WIDTH = 40;

/** Ширина по умолчанию для колонок без явно заданного width. */
const DEFAULT_COLUMN_WIDTH = 150;

const STORAGE_PREFIX = 'editable-grid-cols:';

type ColumnWithOptionalWidth = { key: string; width?: number };

function loadPersistedWidths(persistId: string): Record<string, number> | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + persistId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed as Record<string, number>;
    }
  } catch {
    // ignore
  }
  return null;
}

function savePersistedWidths(persistId: string, widths: Record<string, number>): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + persistId, JSON.stringify(widths));
  } catch {
    // ignore
  }
}

/**
 * Управляет шириной колонок таблицы с поддержкой ресайза через drag-handle.
 *
 * Начальные ширины берутся из column.width. Колонки без явной ширины получают
 * DEFAULT_COLUMN_WIDTH — это гарантирует стабильный рендер с первого же кадра
 * без скачков, которые возникли бы при DOM-измерении после mount.
 *
 * Если передан persistId — ширины сохраняются в localStorage при завершении ресайза
 * и восстанавливаются при следующем маунте.
 *
 * Поведение ресайза: свободный (free resize) — при растяжении колонки A
 * ширина колонки B не меняется, таблица становится шире.
 */
export function useColumnResize(
  columns: readonly ColumnWithOptionalWidth[],
  persistId?: string | null
) {
  const persistIdRef = useRef(persistId);
  persistIdRef.current = persistId;

  const widthsRef = useRef<Record<string, number> | null>(null);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const persisted = persistId ? loadPersistedWidths(persistId) : null;
    const widths: Record<string, number> = {};
    for (const col of columns) {
      widths[col.key] = persisted?.[col.key] ?? col.width ?? DEFAULT_COLUMN_WIDTH;
    }
    widthsRef.current = widths;
    return widths;
  });

  /** Возвращает mousedown-handler для resize-ручки конкретной колонки. */
  const getResizeHandler = (columnKey: string) => (event: React.MouseEvent) => {
    event.preventDefault();

    const startX = event.clientX;
    const startWidth = columnWidths[columnKey] ?? DEFAULT_COLUMN_WIDTH;

    const onMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startX;
      const newWidth = Math.max(MIN_COLUMN_WIDTH, Math.round(startWidth + delta));
      setColumnWidths(prev => {
        const next = { ...prev, [columnKey]: newWidth };
        widthsRef.current = next;
        return next;
      });
    };

    const onMouseUp = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);

      // Сохраняем в localStorage после завершения ресайза
      if (persistIdRef.current && widthsRef.current) {
        savePersistedWidths(persistIdRef.current, widthsRef.current);
      }
    };

    // Блокируем выделение текста и фиксируем курсор на время drag
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return { columnWidths, getResizeHandler };
}
